/**
 * Form Data Transformers for Step 2
 * 
 * Handles transformation between form state, store data, and AI responses
 * Adapted for React Hook Form (JobPostFormData)
 */

import { JobPostFormData } from '../../../schemas/jobPostValidation';
import { JobDescriptionEntity, Requirements } from '../../../../../Entities/JobDescription';
import { AdditionalRequirementDto } from '../../../../../Dto/RequirementCategory.dto';

/**
 * Local state type for requirements (used in component)
 */
export interface RequirementState {
  id: string | number;
  description: string;
  mustMatch: boolean;
  order: number;
}

/**
 * Transform requirements array to Step2Data (form format)
 * Sets topic to empty string per FR-009
 * Preserves original numeric IDs when possible, ensures uniqueness for duplicates
 */
export const transformStep2ToStore = (requirements: RequirementState[]): JobPostFormData['step2'] => {
  // Track used IDs to ensure uniqueness
  const usedIds = new Set<number>();
  // Map to track original ID to new ID mapping (for string IDs or duplicates)
  const idMapping = new Map<string | number, number>();
  
  const transformedRequirements: Requirements[] = requirements.map((req, index) => {
    let id: number;
    
    if (typeof req.id === 'number') {
      // If it's already a number, try to preserve it if unique
      if (!usedIds.has(req.id)) {
        id = req.id;
        usedIds.add(id);
      } else {
        // Duplicate numeric ID - find next available ID starting from the original
        id = req.id;
        while (usedIds.has(id)) {
          id++;
        }
        usedIds.add(id);
        idMapping.set(req.id, id);
      }
    } else {
      // For string IDs, check if we already mapped this string ID
      if (idMapping.has(req.id)) {
        id = idMapping.get(req.id)!;
      } else {
        // Find next available numeric ID
        id = index + 1;
        while (usedIds.has(id)) {
          id++;
        }
        usedIds.add(id);
        idMapping.set(req.id, id);
      }
    }
    
    return {
      id,
      topic: '', // Always empty string per FR-009
      description: req.description.trim(),
      mustMatch: req.mustMatch,
    };
  });

  return {
    requirements: transformedRequirements,
    additionalRequirements: [], // Additional requirements handled separately if needed
  };
};

/**
 * Transform Step2Data to local state (for component)
 */
export const transformStoreToStep2 = (step2Data: JobPostFormData['step2'] | null | undefined): RequirementState[] => {
  if (!step2Data || !step2Data.requirements) {
    return [];
  }

  return step2Data.requirements.map((req, index) => ({
    id: req.id,
    description: req.description || '',
    mustMatch: req.mustMatch ?? false,
    order: index,
  }));
};

/**
 * Map AI-generated JobDescriptionEntity to Step2Data
 * Used when AI generates requirements in Step 0
 */
export const mapJobDescriptionToStep2 = (jobPost: JobDescriptionEntity | null): JobPostFormData['step2'] => {
  if (!jobPost) {
    return {
      requirements: [],
      additionalRequirements: [],
    };
  }

  const requirements: Requirements[] = (jobPost.requirements || []).map((req, index) => ({
    id: req.id || index + 1,
    topic: '', // Always empty string per FR-009
    description: req.description || req.topic || '', // Use description, fallback to topic
    mustMatch: req.mustMatch ?? true,
  }));

  return {
    requirements,
    additionalRequirements: jobPost.additionalRequirements || [],
  };
};

/**
 * Map AI response to Step2Data format
 * Used when AI refines Step 2 requirements via ?step=requirements
 */
export const mapAIResponseToStep2Data = (aiResponse: any): JobPostFormData['step2'] => {
  if (!aiResponse) {
    return {
      requirements: [],
      additionalRequirements: [],
    };
  }

  // AI may return requirements in different formats
  const requirements: Requirements[] = (aiResponse.requirements || []).map((req: any, index: number) => ({
    id: req.id || index + 1,
    topic: '', // Always empty string per FR-009
    description: req.description || req.text || req.content || '',
    mustMatch: req.mustMatch ?? true,
  }));

  return {
    requirements,
    additionalRequirements: aiResponse.additionalRequirements || [],
  };
};

/**
 * Merge AI-generated data with existing user data
 * Preserves user modifications, only updates AI-generated fields
 */
export const mergeAIWithUserData = (
  userData: JobPostFormData['step2'] | null,
  aiData: JobPostFormData['step2']
): JobPostFormData['step2'] => {
  if (!userData) {
    return aiData;
  }

  // For requirements, AI data replaces user data when regenerated
  // But we preserve the structure
  return {
    requirements: aiData.requirements,
    additionalRequirements: (aiData.additionalRequirements && aiData.additionalRequirements.length > 0)
      ? aiData.additionalRequirements 
      : (userData?.additionalRequirements || []),
  };
};


